import { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, StickyNote, Check, AlertCircle } from 'lucide-react';
import { Keypad } from './Keypad.jsx';
import { CustomerPicker } from './CustomerPicker.jsx';
import { PaySheet } from './PaySheet.jsx';
import { Loading, StatusBadge } from '../components/ui/index.js';
import { useResource, useMutation } from '../hooks/useApi.js';
import { useAuth } from '../lib/auth.jsx';
import { http } from '../lib/api.js';
import { formatMoney, formatTimestamp } from '../lib/format.js';
import { playOutcome } from '../lib/sound.js';

const MAX_DIGITS = 9;

/**
 * The till.
 *
 * Full screen and outside the dashboard shell on purpose: the sidebar is thirty
 * per cent of a tablet, and none of it is reachable one-handed. Everything here
 * is sized for a thumb and legible at arm's length across a counter.
 *
 * It creates no payment machinery of its own — a sale rung up here is the same
 * Payment and the same checkout session as one created through the API. See
 * services/posService.js.
 */
export function PosPage() {
  const { user } = useAuth();
  const { data: context, loading, error: contextError, refetch } = useResource('/dashboard/pos/context');

  const [digits, setDigits] = useState('');
  const [customer, setCustomer] = useState({ phone: '', name: '', id: null });
  const [note, setNote] = useState('');
  const [fields, setFields] = useState({});
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sale, setSale] = useState(null);
  const [fieldError, setFieldError] = useState(null);

  const amount = Number(digits || '0');
  const currency = context?.currency ?? 'USD';
  const accent = context?.accentColor || 'rgb(var(--ink))';

  const missingRequired = useMemo(
    () => (context?.customFields ?? [])
      .filter((f) => f.required && !String(fields[f.key] ?? '').trim())
      .map((f) => f.label),
    [context, fields],
  );

  const reset = useCallback(() => {
    setDigits('');
    setCustomer({ phone: '', name: '', id: null });
    setNote('');
    setFields({});
    setSale(null);
    setSheetOpen(false);
    setFieldError(null);
    // Today's total and the recent list are stale the moment a sale completes.
    refetch();
  }, [refetch]);

  const { mutate: ring, loading: ringing, error: ringError, reset: clearError } = useMutation(
    async (method) => {
      const result = await http.post('/dashboard/pos/sales', {
        amount,
        currency,
        method,
        ...(customer.phone || customer.name
          ? {
              customer: {
                ...(customer.phone ? { phone: customer.phone } : {}),
                ...(customer.name ? { name: customer.name } : {}),
              },
            }
          : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(Object.keys(fields).length ? { customFields: fields } : {}),
      });
      setSale(result);
      // A sale settled at the counter never reaches the poller, so it has to
      // make its own noise. One awaiting confirmation is not a success and
      // must not sound like one.
      if (result?.payment?.status === 'succeeded') playOutcome('approved');
      return result;
    },
  );

  // Physical keyboards exist on counters too, and a cashier who can type is
  // faster than one who taps.
  useEffect(() => {
    if (sheetOpen) return undefined;
    const onKey = (e) => {
      // Never steal a keystroke from a field somebody is typing in.
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
      if (/^[0-9]$/.test(e.key)) setDigits((d) => (d.length >= MAX_DIGITS ? d : (d + e.key).replace(/^0+/, '')));
      else if (e.key === 'Backspace') setDigits((d) => d.slice(0, -1));
      else if (e.key === 'Escape') setDigits('');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetOpen]);

  const charge = () => {
    if (amount <= 0) return;
    if (missingRequired.length > 0) {
      setFieldError(`Fill in ${missingRequired.join(' and ')} first.`);
      return;
    }
    setFieldError(null);
    clearError();
    setSale(null);
    setSheetOpen(true);
  };

  if (loading) {
    return <div className="grid min-h-screen place-items-center bg-surface-2"><Loading label="Opening the till…" /></div>;
  }

  if (contextError) {
    return (
      <div className="grid min-h-screen place-items-center bg-surface-2 px-6">
        <div className="max-w-sm text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-red-500" aria-hidden="true" />
          <h1 className="mt-4 text-lg font-semibold text-ink">The till could not open</h1>
          <p className="mt-2 text-sm text-muted">{contextError.message}</p>
          <Link to="/dashboard" className="btn-primary mt-6 inline-flex">Back to the dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface-2">
      {/* ------------------------------- header ------------------------------- */}
      <header
        className="flex h-[64px] shrink-0 items-center justify-between gap-4 border-b bg-surface px-4 sm:px-6"
        style={{ borderColor: 'rgb(var(--line))' }}
      >
        {/*
          * The merchant's brand first, ours second. This is a screen a shop puts
          * on a counter facing their own customers — it used to carry only a
          * platform logo and a grey chip, which is our branding on their till.
          */}
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[15px] font-semibold text-white"
            style={{ background: accent }}
            aria-hidden="true"
          >
            {(context.displayName ?? 'S').charAt(0).toUpperCase()}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-[15px] font-semibold leading-tight text-ink">
              {context.displayName}
            </span>
            <span className="block text-[11px] leading-tight text-muted">Point of sale</span>
          </span>
        </div>

        <div className="flex items-center gap-4">
          {/* Today's takings. A till that cannot tell you what it took today is
              a calculator. */}
          <div className="hidden text-right sm:block">
            <p className="text-[10px] uppercase tracking-wide text-faint">Today</p>
            <p className="tabular text-sm font-semibold leading-tight text-ink">
              {context.today.length === 0
                ? formatMoney(0, currency)
                : context.today.map((t) => formatMoney(t.total, t.currency)).join(' · ')}
              <span className="ml-1.5 font-normal text-muted">
                ({context.today.reduce((n, t) => n + t.count, 0)})
              </span>
            </p>
          </div>

          <span className="hidden h-7 w-px bg-line sm:block" aria-hidden="true" />

          <span className="hidden text-xs text-muted md:inline">{user?.name || user?.email}</span>
          <Link to="/dashboard" className="btn-ghost btn-sm" aria-label="Exit point of sale">
            <X className="h-3.5 w-3.5" /> Exit
          </Link>
        </div>
      </header>

      {/* -------------------------------- body -------------------------------- */}
      {/* `items-start` was leaving the bottom third of a tablet blank. The pad
          column now stretches, so the Charge button sits where a thumb is. */}
      <div className="mx-auto grid w-full max-w-5xl flex-1 gap-5 p-4 sm:p-6 lg:grid-cols-[1fr_340px]">
        {/* ---- left: what is being sold ---- */}
        <div className="flex flex-col gap-4">
          <div
            className="flex flex-col justify-center rounded-2xl border bg-surface px-6 py-8"
            style={{ borderColor: 'rgb(var(--line))' }}
          >
            <p className="eyebrow">Amount</p>
            <motion.p
              key={digits}
              initial={{ y: -3 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.12 }}
              className={`tabular mt-1 text-[44px] font-semibold leading-none tracking-tight sm:text-[56px] ${
                amount > 0 ? 'text-ink' : 'text-faint'
              }`}
            >
              {formatMoney(amount, currency)}
            </motion.p>
          </div>

          {/* ---- customer ---- */}
          <div className="rounded-2xl border bg-surface p-5" style={{ borderColor: 'rgb(var(--line))' }}>
            <div className="mb-3 flex items-center gap-2">
              <User className="h-3.5 w-3.5 text-faint" aria-hidden="true" />
              <p className="eyebrow">Customer</p>
              <span className="text-[11px] text-faint">optional</span>
            </div>

            {/*
              * Two bare text boxes used to sit here, which meant a returning
              * customer had to be retyped from memory every visit and the
              * customer list the platform keeps was invisible from the till.
              */}
            <CustomerPicker value={customer} onChange={setCustomer} />

            <p className="mt-2 text-xs text-muted">
              Optional. Leave it blank for an anonymous sale — no customer record is created.
            </p>
          </div>

          {/* ---- the merchant's own fields ---- */}
          <div className="rounded-2xl border bg-surface p-5" style={{ borderColor: 'rgb(var(--line))' }}>
              <div className="mb-3 flex items-center gap-2">
                <StickyNote className="h-3.5 w-3.5 text-faint" aria-hidden="true" />
                <p className="eyebrow">Details</p>
              </div>

              {context.customFields.length > 0 && (
                <div className="mb-3 grid gap-3 sm:grid-cols-2">
                  {context.customFields.map((field) => (
                    <label key={field.key} className="block">
                      <span className="mb-1 block text-xs font-medium text-ink-soft">
                        {field.label}
                        {field.required && <span className="ml-0.5 text-red-500">*</span>}
                      </span>
                      {field.type === 'select' ? (
                        <select
                          className="input"
                          value={fields[field.key] ?? ''}
                          onChange={(e) => setFields((f) => ({ ...f, [field.key]: e.target.value }))}
                        >
                          <option value="">—</option>
                          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input
                          type={field.type === 'number' ? 'number' : 'text'}
                          inputMode={field.type === 'number' ? 'decimal' : undefined}
                          className="input"
                          value={fields[field.key] ?? ''}
                          onChange={(e) => setFields((f) => ({ ...f, [field.key]: e.target.value }))}
                        />
                      )}
                    </label>
                  ))}
                </div>
              )}

              <textarea
                rows={2}
                className="input leading-relaxed"
                placeholder="Note — anything worth remembering about this sale"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                aria-label="Note"
              />

              {context.customFields.length === 0 && (
                <p className="mt-2 text-xs text-muted">
                  Want structured fields here — a table number, a salesperson?{' '}
                  <Link to="/settings/custom-fields" className="font-medium text-accent-ink hover:underline">
                    Set them up
                  </Link>
                  .
                </p>
              )}
          </div>

          {/* ---- what this till has already done ---- */}
          {context.recent.length > 0 && (
            <div className="rounded-2xl border bg-surface p-5" style={{ borderColor: 'rgb(var(--line))' }}>
              <div className="mb-3 flex items-center justify-between">
                <p className="eyebrow">Recent sales</p>
                <Link to="/payments" className="text-xs font-medium text-accent-ink hover:underline">
                  See all
                </Link>
              </div>

              <ul className="divide-y" style={{ borderColor: 'rgb(var(--line-soft))' }}>
                {context.recent.map((sale) => (
                  <li key={sale.id} className="flex items-center justify-between gap-3 py-2.5">
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-ink">
                        {sale.customer ?? 'Walk-in'}
                      </span>
                      <span className="block text-xs capitalize text-muted">
                        {sale.method === 'card' ? 'Card' : sale.method.replace(/_/g, ' ')}
                        {' · '}
                        {formatTimestamp(sale.createdAt)}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2.5">
                      <StatusBadge status={sale.status} size="sm" />
                      <span className="tabular text-sm font-medium text-ink">
                        {formatMoney(sale.amount, sale.currency)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ---- right: the pad ---- */}
        <div className="flex flex-col gap-3">
          <Keypad
            disabled={sheetOpen}
            onDigit={(d) => setDigits((v) => {
              const next = (v + d).replace(/^0+/, '');
              return next.length > MAX_DIGITS ? v : next;
            })}
            onBackspace={() => setDigits((v) => v.slice(0, -1))}
            onClear={() => setDigits('')}
          />

          <AnimatePresence>
            {fieldError && (
              <motion.p
                role="alert"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden text-center text-xs font-medium text-red-600"
              >
                {fieldError}
              </motion.p>
            )}
          </AnimatePresence>

          <motion.button
            type="button"
            onClick={charge}
            disabled={amount <= 0}
            whileTap={amount > 0 ? { scale: 0.985 } : {}}
            className="btn-primary h-16 w-full text-base disabled:opacity-40"
          >
            <Check className="h-4 w-4" aria-hidden="true" />
            Charge {amount > 0 ? formatMoney(amount, currency) : ''}
          </motion.button>
        </div>
      </div>

      <PaySheet
        open={sheetOpen}
        amount={amount}
        currency={currency}
        sale={sale}
        context={context}
        busy={ringing}
        error={ringError}
        onPick={(method) => ring(method).catch(() => {})}
        onClose={() => { setSheetOpen(false); setSale(null); }}
        onDone={reset}
      />
    </div>
  );
}

export default PosPage;
